import * as React from "react";

export function useDragCallbacks(
    onStart: ((loc: readonly [number, number]) => void) | undefined,
    onMove: ((loc: readonly [number, number], delta: readonly [number, number]) => void) | undefined,
    onEnd: (() => void) | undefined
) {
    const [touchStart, setTouchStart] = React.useState<readonly [number, number]>([0, 0]);
    const [dragging, setDragging] = React.useState(false);

    const onMouseDown = React.useCallback(
        (ev: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
            let x = 0;
            let y = 0;
            if (ev.type === "touchstart") {
                x = (ev as React.TouchEvent<HTMLElement>).touches[0].clientX;
                y = (ev as React.TouchEvent<HTMLElement>).touches[0].clientY;
            } else {
                x = (ev as React.MouseEvent<HTMLElement>).clientX;
                y = (ev as React.MouseEvent<HTMLElement>).clientY;
            }
            const r = [x, y] as const;
            setTouchStart(r);
            setDragging(true);
            onStart?.(r);
        },
        [onStart]
    );

    const onMouseMove = React.useCallback(
        (ev: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
            if (!dragging) return;
            let x = 0;
            let y = 0;
            if (ev.type === "touchmove") {
                x = (ev as React.TouchEvent<HTMLElement>).touches[0].clientX;
                y = (ev as React.TouchEvent<HTMLElement>).touches[0].clientY;
            } else {
                x = (ev as React.MouseEvent<HTMLElement>).clientX;
                y = (ev as React.MouseEvent<HTMLElement>).clientY;
            }
            onMove?.([x, y], [x - touchStart[0], y - touchStart[1]]);
        },
        [dragging, onMove, touchStart]
    );

    const onMouseUp = React.useCallback(
        (_ev: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
            if (!dragging) return;
            setDragging(false);
            onEnd?.();
        },
        [dragging, onEnd]
    );

    return {
        onMouseDownCapture: onMouseDown,
        onMouseMoveCapture: onMouseMove,
        onMouseUpCapture: onMouseUp,
        onMouseOutCapture: onMouseUp,
        onTouchStartCapture: onMouseDown,
        onTouchMoveCapture: onMouseMove,
        onTouchEndCapture: onMouseUp,
        onTouchCancelCapture: onMouseUp,
    };
}
